/*
 * Everything that reads a notification's payload. Nothing here imports from notifications.model:
 * that file imports the summary builders from here, and a runtime import back would make the load
 * order decide whether its enums exist yet.
 */

export interface BuildMigrationNotificationPayload {
  sourceRootBuildTag: string
  targetRootBuildTag: string
  totalMigrationInstances: number
  totalFailedMigrationInstances?: number
}

/** What an export, of a version or a build, was run with. */
export interface ExportNotificationPayload {
  flowxVersion: string
  includeBinaries: boolean
}

/** One resource the target FlowX version does not support, left out of the exported archive. */
export interface ExcludedResource {
  resourceType: string
  resourceName: string
}

/**
 * The `details` of an export, of a version or a build, that finished with a warning: every resource
 * it left out.
 */
export interface ExportNotificationDetails {
  resources: ExcludedResource[]
}

export enum SummarySegmentType {
  TEXT = 'TEXT',
  ID = 'ID', // Truncated on the card, in full in its tooltip and in search.
  ARROW = 'ARROW',
  DOT = 'DOT',
}

export type SummarySegment =
  | { type: SummarySegmentType.TEXT | SummarySegmentType.ID; text: string }
  | { type: SummarySegmentType.ARROW | SummarySegmentType.DOT; text?: undefined }

/**
 * The payload's facts as the card lists them (`1.6.2 → 1.6.3 · 128 instances`). Empty when the
 * payload has nothing to show.
 */
export type NotificationSummary = SummarySegment[]

/** `countNoun` labels the count; it comes from the notification's type and status. */
export const buildMigrationSummary = (
  countNoun: string | undefined,
  {
    sourceRootBuildTag,
    targetRootBuildTag,
    totalMigrationInstances,
    totalFailedMigrationInstances,
  }: BuildMigrationNotificationPayload
): NotificationSummary => {
  const migrated = totalMigrationInstances - (totalFailedMigrationInstances ?? 0)

  return sourceRootBuildTag && targetRootBuildTag
    ? [
        textSegment(sourceRootBuildTag),
        ARROW_SEGMENT,
        textSegment(targetRootBuildTag),
        ...(countNoun ? [DOT_SEGMENT, textSegment(`${migrated} ${countNoun}`)] : []),
      ]
    : []
}

// A build is addressed by its app version, so the summary reads the same for both.
export const buildExportSummary = (
  { appVersionId, branchName }: { appVersionId: string; branchName?: string },
  { flowxVersion, includeBinaries }: ExportNotificationPayload
): NotificationSummary => [
  ...(branchName ? [textSegment(branchName)] : []),
  idSegment(appVersionId),
  ARROW_SEGMENT,
  textSegment(flowxVersion),
  DOT_SEGMENT,
  textSegment(includeBinaries ? 'media included' : 'media excluded'),
]

// Reads no payload: an import, of a version or a build, carries nothing beyond the envelope's version
// and branch.
export const buildImportSummary = ({
  appVersionId,
  branchName,
}: {
  appVersionId: string
  branchName?: string
}): NotificationSummary => [
  ...(branchName ? [textSegment(branchName)] : []),
  idSegment(appVersionId),
]

const textSegment = (text: string): SummarySegment => ({ type: SummarySegmentType.TEXT, text })

const idSegment = (text: string): SummarySegment => ({ type: SummarySegmentType.ID, text })

const ARROW_SEGMENT: SummarySegment = { type: SummarySegmentType.ARROW }

const DOT_SEGMENT: SummarySegment = { type: SummarySegmentType.DOT }
